import * as moment from 'moment';
import { NgbDateStruct, NgbTimeStruct } from '@ng-bootstrap/ng-bootstrap';
import { FormArray, FormGroup } from '@angular/forms';
import { amtFormatTypes, API_DATE_FORMAT, ISO_DATE_STRING } from './shared/global-meta-data';

const divisionMap = {
  1: 5,
  2: 5,
  3: 5,
  4: 5,
  5: 5,  // 11111 10 thousand
  6: 5,  // 111111 1 Lac
  7: 5,  // 1111111 10 Lac
  8: 7,  // 11111111 1 Cr
  9: 7,  // 111111111 10 Cr
  10: 7, // 1111111111 100 Cr
  11: 7, // 11111111111 1000 Cr
  12: 7, // 111111111111 10 Bil
  13: 7,
  14: 7,
  15: 7,
  16: 7,
  17: 7,
  18: 7,
  19: 7
};

const divisionFcMap = {
  1: 6,
  2: 6,
  3: 6,
  4: 6,
  5: 6,  // 11111 10 th
  6: 6,  // 111111 100 th
  7: 6,  // 1111111 1 Mil
  8: 6,  // 11111111 10 Mil
  9: 6,  // 111111111 100 Mil
  10: 9, // 1111111111 1 Bil
  11: 9, // 11111111111 10 Bil
  12: 9, // 111111111111 100 Bil
  13: 9,
  14: 9,
  15: 9,
  16: 9,
  17: 9,
  18: 9,
  19: 9,
  default: 6
};

const holidaysList2018 = [];

export const ISINRegex = /^(IN)[A-G]{1}[A-Z0-9]{4}14([A-Z0-9]{3})$/gm;
export const indiaPhoneNumberRegex = /^([+]91)[0-9]{10}$/;
export const API_DURATION_REGEX = '^([0-9]{1,4})\\s+[0-9][0-9]:[0-9][0-9]:[0-9][0-9]$';
// DD hh:mm:ss or DDD hh:mm:ss

const digitCountDenominationMap = {
  5: 'Lakhs',
  7: 'Cr'
};

const digitCountDenominationFcMap = {
  6: 'Mil',
  9: 'Bil',
  default: 'Mil'
};

export function setHolidays(holidays) {
  if (!holidaysList2018.length && holidays && holidays.length) {
    holidays.forEach(holiday => holidaysList2018.push(holiday));
  }
}

/**
 * This function accepts the date object returned by the ng-bootstrap date picker and transforms it
 * into "API_DATE_FORMAT" and returns it.
 *
 * @param datepickerObject - Date object from ng-bootstrap.
 * @returns - Date string in API_DATE_FORMAT.
 */

export function getTransformedDate(dayObj, format, setEnd) {
  let returnVal = '';
  if (!dayObj) {
    return null;
  }
  if (!dayObj.day || !dayObj.month || !dayObj.year) {
    return null;
  }
  if (format && format === ISO_DATE_STRING) {
    returnVal = setEnd ? moment({
      day: dayObj.day,
      month: dayObj.month - 1,
      year: dayObj.year
    }).endOf('day').format() : moment({
      day: dayObj.day,
      month: dayObj.month - 1,
      year: dayObj.year
    }).format();
  } else {
    returnVal = moment({
      day: dayObj.day,
      month: dayObj.month - 1,
      year: dayObj.year
    }).format(API_DATE_FORMAT);
  }
  return returnVal;
}

/**
 * Return the formatted date string from date and time calendar objects
 * @param dateObj ngb calendar date picker form input object
 * @param timeObj ngb calendar time picker form input object
 * @param setEnd set formatted date string to end of day
 * @param dateFormat parse the string in specified format or iso format if date format is not passed
 */
export function getTransformedDateTime(dateObj: NgbDateStruct, timeObj: NgbTimeStruct,
  setEnd, dateFormat = null) {
  const momentObj = getMomentFromCalendarDateTime(dateObj, timeObj);
  const returnDate = setEnd ?
    momentObj.endOf('day') :
    momentObj;
  return dateFormat ? returnDate.format(dateFormat) : returnDate.format();
}

/**
 * Return the moment object from date and time calendar objects
 * @param dateObj ngb calendar date picker form input object
 * @param timeObj ngb calendar time picker form input object
 */
export function getMomentFromCalendarDateTime(dateObj: NgbDateStruct, timeObj: NgbTimeStruct) {
  if (!dateObj) {
    return moment();
  }
  const month = dateObj.month - 1;
  const year = dateObj.year;
  const day = dateObj.day;
  return moment(Object.assign({}, { year, month, day }, timeObj || {}));
}

/**
 * This function returns the proper amount after appending the digits after amount based on amountInWords.
 *
 * @param amount - Value of the amount mentioned.
 * @param amountInWords - Value of the amount mentioned in. For example: Cr, Lakhs, Th etc.
 * @returns - Proper amount number after appending digits.
 */
export function getTransformedNumber(amount, amountInWords) {
  switch (amountInWords) {
    case 'Bil': return amount * 1000000000;
    case 'Mil': return amount * 1000000;
    case 'Cr': return amount * 10000000;
    case 'Lakhs': return amount * 100000;
    default: return amount || 0;
  }
}

/**
 * converts javascript date to ngb bootstrap calendar format
 * @param jsDate javascript date object
 */
export function getCalendarTimeObj(jsDate): NgbTimeStruct {
  return {
    hour: jsDate.getHours(),
    minute: jsDate.getMinutes(),
    second: jsDate.getSeconds()
  };
}

/**
 * For the given jsDate, returns the NgbDateStruct object for use in NgbDatePicker.
 * @param jsDate - Date in the Javascript format.
 */
export function getCalendarDateObj(jsDate): NgbDateStruct {
  return {
    year: jsDate.getFullYear(),
    month: jsDate.getMonth() + 1,
    day: jsDate.getDate()
  };
}

/**
 * return moment object from given calendar date object
 * @param calendarObj ngb bootstrap date object
 */
export function getDateFromCalendarObj(calendarObj): moment.Moment {
  if (!calendarObj) {
    return moment();
  }
  return moment({
    day: calendarObj.day,
    month: calendarObj.month - 1,
    year: calendarObj.year
  });
}

/**
 * check if the calendar object corresponds to today
 * @param calendarDateObj the calendar object
 */
export function isCalendarDateToday(calendarDateObj): boolean {
  const now = new Date();
  return calendarDateObj && calendarDateObj.day === now.getDate() &&
    calendarDateObj.month === (now.getMonth() + 1) &&
    calendarDateObj.year === now.getFullYear();
}

/**
 * check if time from calendar object is less than current time.
 */
export function isTimeLessThanNow(calendarTimeObj): boolean {
  if (!calendarTimeObj) {
    return;
  }
  const now = new Date();
  const calTimeInSecs = calendarTimeObj.hour * 60 * 60 + calendarTimeObj.minute * 60 + calendarTimeObj.second;
  const nowInSecs = now.getHours() * 60 * 60 + now.getMinutes() * 60 + now.getSeconds();
  return calTimeInSecs < nowInSecs;
}

export function getDiffDaysFromBaseDate(baseDate, dateToDiff, readability): number {
  const days = dateToDiff.diff(baseDate.startOf('day'), 'days');
  let returnVal;

  if (readability) {
    returnVal = days >= 0 ? days + ' Days from now.' : -(days) + ' Days ago.';
  } else {
    returnVal = days;
  }

  return returnVal;
}


export function amountFormatter(value, prefix, returnType, amountFormatType = amtFormatTypes.INDIAN): any {
  const sign = (Math.sign(value) < 0) ? '-' : '';
  const unsignedValue = Math.abs(value);
  const digits = String(unsignedValue).length;
  let reducedValue;

  const divisionMapToUse = amountFormatType === amtFormatTypes.WESTERN ? divisionFcMap : divisionMap;
  const digitCountDenomMapToUse = amountFormatType === amtFormatTypes.WESTERN ? digitCountDenominationFcMap : digitCountDenominationMap;


  reducedValue = unsignedValue / Math.pow(10, divisionMapToUse[digits]);
  reducedValue = isInteger(reducedValue) ? reducedValue : reducedValue.toFixed(2);
  if (returnType === 'object') {
    return {
      sign,
      reducedValue,
      denomination: digitCountDenomMapToUse[divisionMapToUse[digits]]
    };
  } else {
    return reducedValue === 0 ?
      '0' :
      (prefix || '') + ' ' + sign + reducedValue + ' ' +
      digitCountDenomMapToUse[divisionMapToUse[digits]];
  }
}

export function isInteger(x) {
  return (typeof x === 'number') && (x % 1 === 0);
}

/**
 * For a given
 * @param amount - Amount value
 */
export function getAmountObject(amount) {
  if (!amount) {
    amount = 0;
  }
  const unsignedValue = Math.abs(amount);
  const digits = String(unsignedValue).length;
  let reducedValue;

  reducedValue = unsignedValue / Math.pow(10, divisionMap[digits]);
  reducedValue = isInteger(reducedValue) ? reducedValue : reducedValue.toFixed(2);

  return { amount: reducedValue, amountIn: digitCountDenominationMap[divisionMap[digits]] };
}

export function isWeekendAndHolidayDisabled(date: NgbDateStruct) {
  if (!date) {
    return false;
  }

  const currentDate = moment({
    year: date.year,
    month: date.month - 1,
    day: date.day,
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0
  });

  const isBusinessHoliday = holidaysList2018.map((holiday) => {
    return moment(Object.assign({},
      holiday, {
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0
    }));
  }).find(matchDay => {
    return currentDate.isSame(matchDay);
  });

  const isWeekend = currentDate.isoWeekday() === 6 || currentDate.isoWeekday() === 7;
  return isWeekend || (isBusinessHoliday ? true : false);
}


export function timeStringComparator(compareField, direction) {
  return function (objectA, objectB) {
    const compareFieldPar = compareField;
    const directionPar = direction;
    if (moment(objectA[compareFieldPar]).isBefore(moment(objectB[compareFieldPar]))) {
      return directionPar === 'asc' ? -1 : 1;
    } else if (moment(objectA[compareFieldPar]).isAfter(moment(objectB[compareFieldPar]))) {
      return directionPar === 'asc' ? 1 : -1;
    } else {
      return 0;
    }
  };
}

export function findObjFromValue(obj, val) {
  const objFound = obj.find((objEntry) => {
    return objEntry.value === val;
  });
  return objFound !== -1 ? objFound : null;
}

export function clearFormArray(formArray: FormArray) {
  while (formArray.length !== 0) {
    formArray.removeAt(0);
  }
}

/**
 * First marks the form group controls to modified.
 * Then triggers validation and value changes for each form control
 * @param inpFormGroup form group to modify and trigger validation for,
 */
export function markFormControlsDirty(inpFormGroup: FormGroup) {
  Object.keys(inpFormGroup.controls).forEach(key => {
    inpFormGroup.get(key).markAsDirty();
  });
  Object.keys(inpFormGroup.controls).forEach(key => {
    inpFormGroup.get(key).updateValueAndValidity({ onlySelf: true, emitEvent: false });
  });
}

export function toInteger(value: any): number {
  return parseInt(`${value}`, 10);
}

export function isNumber(value: any): value is number {
  return !isNaN(toInteger(value));
}

export function padNumber(value: number) {
  if (isNumber(value)) {
    return `0${value}`.slice(-2);
  } else {
    return '';
  }
}

export function getIeVersion() {
  let ieVersion = null;
  const userAgentStr = window.navigator.userAgent;
  const msie = userAgentStr.indexOf('MSIE ');
  const trident = userAgentStr.indexOf('Trident/');
  const legacyEdge = userAgentStr.indexOf('Edge/');
  const newEdge = userAgentStr.indexOf('Edg/');

  if (msie > 0) {
    // IE 10 or older => return version number
    ieVersion = parseInt(userAgentStr.substring(msie + 5, userAgentStr.indexOf('.', msie)), 10);
  }

  if (trident > 0) {
    // IE 11 => return version number
    const tridentVersion = userAgentStr.indexOf('rv:');
    ieVersion = parseInt(userAgentStr.substring(tridentVersion + 3,
      userAgentStr.indexOf('.', tridentVersion)), 10);
  }

  if (legacyEdge > 0) {
    // Legacy Edge => return version number
    ieVersion = parseInt(userAgentStr.substring(legacyEdge + 5,
      userAgentStr.indexOf('.', legacyEdge)), 10);
  }

  if (newEdge > 0) {
    // New Edge => return version number
    ieVersion = parseInt(userAgentStr.substring(newEdge + 5,
      userAgentStr.indexOf('.', newEdge)), 10);
  }

  // other browser
  return ieVersion;
}

export function isMicrosoftBrowser() {
  return !!getIeVersion();
}

export function uniqArrayByKey(inpArr: Array<any>, key: string) {
  return inpArr.filter((elem, pos, arr: Array<any>) => {
    const elemFirstIdx = arr.findIndex(elemToFind => elemToFind[key] === elem[key]);
    return elemFirstIdx === pos;
  });
}

export function groupBy(inpArray, groupByKey) {
  return inpArray.reduce(function (outputObj, inpItem) {
    (outputObj[inpItem[groupByKey]] = outputObj[inpItem[groupByKey]] || []).push(inpItem);
    return outputObj;
  }, {});
};
